import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import httpClient from '../../config/httpClient';
import { FUNCTIONS_BASE_URL } from '../../config/api';
import SubscriptionCard from './SubscriptionCard';
import BillingToggle from './BillingToggle';
import firebase from '@config/firebase.config';
import './styles.scss';
import { Modal } from '@/components/Modal';
import { CancelSubscriptionModal } from './CancelSubscriptionModal.component';
import { addNotification, notificationStates } from '@/components/Notification';
import { ChangeSubscriptionModal } from './ChangeSubscriptionModal.component';
import { ContactUsModal } from '@/modals/ContactUs/ContactUsModal.component';
import { CheckoutSeatsPlanModal } from './CheckoutSeatsPlanModal.component';
import { nonActiveSubscriptionStates } from '@/layouts';
import { ExistingSubscriptionReplaceModal } from './ExistingSubscriptionReplaceModal.component';
import { ModifySeatsModal } from './ModifySeatsModal.component';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { DomesticPaymentMethodWarningModal } from './DomesticPaymentMethodWarningModal.component';
const database = firebase.firestore();
const razorpayPlanIds = {
  starter: {
    monthly: process.env.REACT_APP_RAZORPAY_PLAN_STARTER_MONTHLY || null,
    quarterly: process.env.REACT_APP_RAZORPAY_PLAN_STARTER_QUARTERLY || null,
    yearly: process.env.REACT_APP_RAZORPAY_PLAN_STARTER_YEARLY || null,
  },
  grow: {
    monthly: process.env.REACT_APP_RAZORPAY_PLAN_GROW_MONTHLY || null,
    quarterly: process.env.REACT_APP_RAZORPAY_PLAN_GROW_QUARTERLY || null,
    yearly: process.env.REACT_APP_RAZORPAY_PLAN_GROW_YEARLY || null,
  },
};

// India (INR) plan IDs (optional; we fall back to default plans if these are unset)
const razorpayPlanIdsIN = {
  starter: {
    monthly: process.env.REACT_APP_RAZORPAY_IN_PLAN_STARTER_MONTHLY || null,
    quarterly: process.env.REACT_APP_RAZORPAY_IN_PLAN_STARTER_QUARTERLY || null,
    yearly: process.env.REACT_APP_RAZORPAY_IN_PLAN_STARTER_YEARLY || null,
  },
  grow: {
    monthly: process.env.REACT_APP_RAZORPAY_IN_PLAN_GROW_MONTHLY || null,
    quarterly: process.env.REACT_APP_RAZORPAY_IN_PLAN_GROW_QUARTERLY || null,
    yearly: process.env.REACT_APP_RAZORPAY_IN_PLAN_GROW_YEARLY || null,
  },
};

const CURRENCY = {
  INR: 'INR',
  USD: 'USD',
};

const getNumberFromEnv = (envName, fallback) => {
  const raw = process.env[envName];
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const fetchGeoJs = async () => {
  const res = await fetch('https://get.geojs.io/v1/ip/geo.json', {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`GeoJS request failed (${res.status})`);
  }
  return res.json();
};

const getCurrencyFromCountryCode = (countryCode) => {
  const normalized = String(countryCode || '').toUpperCase();
  return normalized === 'IN' ? CURRENCY.INR : CURRENCY.USD;
};

const getPlanIdForCurrency = ({ tierId, billingCycle, currencyCode }) => {
  const source =
    currencyCode === CURRENCY.INR ? razorpayPlanIdsIN : razorpayPlanIds;
  const planId = source?.[tierId]?.[billingCycle] || null;
  return planId || razorpayPlanIds?.[tierId]?.[billingCycle] || null;
};

const SubscriptionPage = () => {
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [isChangeSubscriptionModalOpen, setIsChangeSubscriptionModalOpen] =
    useState(false);
  const [isCheckoutSeatsPlanModalOpen, setIsCheckoutSeatsPlanModalOpen] =
    useState(false);
  const [
    isDomesticPaymentMethodWarningModalOpen,
    setIsDomesticPaymentMethodWarningModalOpen,
  ] = useState(false);
  const [
    isExistingSubscriptionReplaceModalOpen,
    setIsExistingSubscriptionReplaceModalOpen,
  ] = useState(false);
  const [isModifySeatsModalOpen, setIsModifySeatsModalOpen] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [geoCountryCode, setGeoCountryCode] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [pendingCurrency, setPendingCurrency] = useState(null);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [geoLookupFailed, setGeoLookupFailed] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const authState = useSelector((state) => ({ ...state.auth }));
  const companyId = authState.profile.activeCompany.companyId;
  const isEnterpriseQueryRef = useRef(false);
  const subscriptionIdToCancelRef = useRef(null);
  const canRenderPlans = Boolean(selectedCurrency) && !isCurrencyModalOpen;
  const {
    currentActivePlanId,
    currentInActivePlanId,
    showCycleEndWarning,
    subscriptionEndDate,
    cancelledAt,
    domesticPaymentMethod,
  } = useMemo(() => {
    let activePlanId = null,
      inactivePlanId = null,
      showCycleEndWarning = false;
    if (subscriptionData?.status === 'active') {
      activePlanId = subscriptionData?.planId;
    } else {
      inactivePlanId = subscriptionData?.planId;
    }
    const subscriptionEndDate = subscriptionData?.subscriptionEndDate
      ?.toDate()
      .toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    const cancelledAt = subscriptionData?.cancelledAt
      ?.toDate()
      .toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    const formattedSubscriptionEndDate = subscriptionEndDate
      ? subscriptionEndDate
      : null;
    const formattedCancelledAt = cancelledAt ? cancelledAt : null;
    if (nonActiveSubscriptionStates.includes(subscriptionData?.status)) {
      showCycleEndWarning = true;
    }
    return {
      currentActivePlanId: activePlanId,
      currentInActivePlanId: inactivePlanId,
      subscriptionEndDate: formattedSubscriptionEndDate,
      showCycleEndWarning,
      cancelledAt: formattedCancelledAt,
      domesticPaymentMethod: subscriptionData?.domesticPaymentMethod,
    };
  }, [subscriptionData]);

  useEffect(() => {
    if (authState?.profile?.subscription) {
      setSubscriptionData(authState?.profile?.subscription);
    }
  }, [authState?.profile?.subscription]);

  useEffect(() => {
    let cancelled = false;
    fetchGeoJs()
      .then((data) => {
        if (cancelled) return;
        const countryCode = data?.country_code || null;
        console.log('GeoJS location info:', data);
        console.log('GeoJS country_code:', countryCode);
        setGeoCountryCode(countryCode);
        const detectedCurrency = getCurrencyFromCountryCode(countryCode);
        setSelectedCurrency(detectedCurrency);
        setPendingCurrency(detectedCurrency);
        setGeoLookupFailed(false);
      })
      .catch((err) => {
        console.warn('GeoJS lookup failed:', err?.message || err);
        if (cancelled) return;
        setSelectedCurrency(null);
        setPendingCurrency(null);
        setGeoLookupFailed(true);
        setIsCurrencyModalOpen(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle success/cancel redirects from Stripe Checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    const canceled = urlParams.get('canceled');

    if (subscriptionStatus === 'success') {
      alert('Subscription activated successfully!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (canceled) {
      alert('Subscription was canceled.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const planIdsForCurrency = useMemo(
    () =>
      selectedCurrency === CURRENCY.INR ? razorpayPlanIdsIN : razorpayPlanIds,
    [selectedCurrency]
  );

  // Subscription plans data
  const subscriptionTiers = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Ideal for Growing Buisness',
      icon: 'ni ni-single-02',
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      features: [
        'Advanced time tracking',
        'Idle Time tracking',
        'Screenshots',
        'Advance Reporting and notification',
        'Advance Admin control ',
        'Support : Mail & Support Ticket',
      ],
      pricing: {
        [CURRENCY.USD]: {
          monthly: getNumberFromEnv('REACT_APP_PLAN_STARTER_MONTHLY_USD', 7),
          quarterly: getNumberFromEnv(
            'REACT_APP_PLAN_STARTER_QUARTERLY_USD',
            18
          ),
          yearly: getNumberFromEnv('REACT_APP_PLAN_STARTER_YEARLY_USD', 60),
        },
        [CURRENCY.INR]: {
          monthly: getNumberFromEnv('REACT_APP_PLAN_STARTER_MONTHLY_INR', 650),
          quarterly: getNumberFromEnv(
            'REACT_APP_PLAN_STARTER_QUARTERLY_INR',
            550
          ),
          yearly: getNumberFromEnv('REACT_APP_PLAN_STARTER_YEARLY_INR', 450),
        },
      },
      popular: false,
      razorpayPlanId: {
        monthly: planIdsForCurrency.starter.monthly,
        quarterly: planIdsForCurrency.starter.quarterly,
        yearly: planIdsForCurrency.starter.yearly,
      },

      stripePriceIds: {
        monthly: 'price_1RzvW4JqrNQXJoMrasYJrT8b',
        quarterly: 'price_1Rzve7JqrNQXJoMr4L1GIIW6',
        yearly: 'price_1Rzve7JqrNQXJoMrbCwtOJfs',
      },
    },
    {
      id: 'grow',
      name: 'Grow',
      description: 'Ideal for growing businesses',
      icon: 'ni ni-chart-bar-32',
      color: '#f093fb',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      features: [
        'Advanced time tracking ',
        'Idle Time tracking ',
        'Screenshots',
        'App Usage tracking  ',
        'Advance Reporting and notification',
        'Advance Admin control ',
        'Attendance tracker',
        'Custom task control',
        'Leave Management',
        'Support : Mail, Phone & Support Ticket',
      ],
      pricing: {
        [CURRENCY.USD]: {
          monthly: getNumberFromEnv('REACT_APP_PLAN_GROW_MONTHLY_USD', 10),
          quarterly: getNumberFromEnv('REACT_APP_PLAN_GROW_QUARTERLY_USD', 27),
          yearly: getNumberFromEnv('REACT_APP_PLAN_GROW_YEARLY_USD', 96),
        },
        [CURRENCY.INR]: {
          monthly: getNumberFromEnv('REACT_APP_PLAN_GROW_MONTHLY_INR', 930),
          quarterly: getNumberFromEnv('REACT_APP_PLAN_GROW_QUARTERLY_INR', 830),
          yearly: getNumberFromEnv('REACT_APP_PLAN_GROW_YEARLY_INR', 730),
        },
      },
      popular: true,
      razorpayPlanId: {
        monthly: planIdsForCurrency.grow.monthly,
        quarterly: planIdsForCurrency.grow.quarterly,
        yearly: planIdsForCurrency.grow.yearly,
      },
      stripePriceIds: {
        monthly: 'price_1RzvX8JqrNQXJoMrfXY8kjAR',
        quarterly: 'price_1RzveuJqrNQXJoMr5W85Mwij',
        yearly: 'price_1RzveuJqrNQXJoMr6trHvwbP',
      },
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Built for larger teams and organizations',
      icon: 'ni ni-badge',
      color: '#4facfe',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      features: [
        'Contact Us',
        'Advanced time tracking',
        'Idle Time tracking ',
        'Screenshots',
        'App Usage tracking ',
        'Advance Reporting and notification',
        'Custom Integration',
        'Advance Admin control ',
        'Attendance tracker ',
        'Custom task control ',
        'Leave Management ',
        'Advance company level settings',
        'Advance Security ',
        'Support : Dedicated Account Manager',
      ],
      popular: false,
      // Your actual Stripe Price IDs
      razorpayPlanId: {
        monthly: null,
        quarterly: null,
        yearly: null,
      },
      stripePriceIds: {
        monthly: 'price_1RzvWjJqrNQXJoMrTzxiLpao',
        quarterly: 'price_1RzvfUJqrNQXJoMroqrwMb9S',
        yearly: 'price_1RzvfUJqrNQXJoMrcYQvvvAC',
      },
    },
  ];

  // Calculate discount percentages
  const getDiscount = (billingCycle) => {
    switch (billingCycle) {
      case 'quarterly':
        return 15;
      case 'yearly':
        return 30;
      default:
        return 0;
    }
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle billing cycle change
  const handleBillingCycleChange = (cycle) => {
    setSelectedBillingCycle(cycle);
  };

  const handleCurrencyChoice = (currencyCode) => {
    setPendingCurrency(currencyCode);
  };

  const handleConfirmCurrencyChoice = () => {
    if (!pendingCurrency) return;
    setSelectedCurrency(pendingCurrency);
    setIsCurrencyModalOpen(false);
  };

  // Handle subscription purchase
  const handleSubscribe = async (plan) => {
    setIsLoading(true);
    try {
      console.log('Debug Subscription: ', authState.user);
      console.log('Debug Subscription: ', companyId);
      const customerResponse = await httpClient.post('/createStripeCustomer', {
        email: authState.profile?.email,
        name: authState.profile?.name,
        userId: authState.profile?.id,
        companyId: companyId,
      });
      const customerResult = customerResponse.data;
      console.log('Debug Subscription: ', customerResult);

      if (!customerResult.success) {
        throw new Error('Failed to create customer');
      }

      const customerId = customerResult.customerId;

      // 2. Create form to submit to backend (for redirect)
      const priceId = plan.stripePriceIds[selectedBillingCycle];
      const successUrl = `${window.location.origin}/dashboard-overview?subscription=success`;
      const cancelUrl = `${window.location.origin}/subscription?canceled=true`;

      // Create a form and submit it to backend for redirect
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${FUNCTIONS_BASE_URL}/createCheckoutSession`;

      // Add form fields
      const fields = {
        priceId,
        customerId,
        companyId,
        successUrl,
        cancelUrl,
      };

      Object.keys(fields).forEach((key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = fields[key];
        form.appendChild(input);
      });

      // Submit form (backend will redirect to Stripe)
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription process. Please try again.');
      setIsLoading(false);
    }
    // Note: setIsLoading(false) is not called on success because page will redirect
  };

  // Handle back navigation - check if user came from suspended page
  const handleBackToDashboard = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromSuspended = urlParams.get('from') === 'suspended';
    const companyId = authState.profile.activeCompany?.companyId;

    if (fromSuspended && companyId) {
      history.push(`/subscription-suspended?companyId=${companyId}`);
    } else {
      history.push('/dashboard-overview');
    }
  };
  const planToChangeRef = useRef(null);
  const chosenPlanRef = useRef(null);
  const addModifySeatsFlag = async () => {
    try {
      await database.collection('companyDatabase').doc(companyId).update({
        'subscriptions.modifySeats': true,
      });
    } catch (error) {
      console.log(error, '>>> index.jsx <<<', 'line-465');
    }
  };
  const handleChangeSubscriptionPlanWithDomesticPaymentMethod = async () => {
    subscriptionIdToCancelRef.current = subscriptionData?.subscriptionId;
    handleRazorpaySubscription({
      plan: planToChangeRef.current,
      totalSeats: subscriptionData?.seats,
      modifySeats: true,
      callback: async () => {
        await handleCancelPlan({
          reload: false,
          subscriptionId: subscriptionIdToCancelRef.current,
          message: 'Previous subscription cancelled successfully.',
        });
        await handleRefundForDomesticPayment();
      },
    });
  };
  const handleChangeSubscriptionPlan = async () => {
    try {
      const plan = planToChangeRef.current;
      if (!plan) {
        addNotification({
          message: 'No plan selected to change.',
          level: notificationStates.warning,
        });
        return;
      }
      if (!selectedCurrency) {
        setIsCurrencyModalOpen(true);
        return;
      }
      setIsLoading(true);
      if (domesticPaymentMethod) {
        handleChangeSubscriptionPlanWithDomesticPaymentMethod();
        return;
      }
      const selectedPlanId = getPlanIdForCurrency({
        tierId: plan.id,
        billingCycle: selectedBillingCycle,
        currencyCode: selectedCurrency,
      });
      if (!selectedPlanId) {
        throw new Error(
          `Razorpay planId missing for tier=${plan.id}, billingCycle=${selectedBillingCycle}`
        );
      }
      const response = await httpClient.post('/changeSubscriptionPlan', {
        subscriptionId: subscriptionData?.subscriptionId,
        planId: selectedPlanId,
        companyId,
      });
      const result = response.data;
      if (result?.success) {
        addNotification({
          message: 'Subscription changed successfully.',
          level: notificationStates.success,
          autoDismiss: 4,
        });
        history.push('/dashboard-overview?subscription=success');
        window.location.reload();
      } else {
        addNotification({
          message:
            result?.error ||
            result?.message ||
            'Could not change subscription plan. Please try again.',
          level: notificationStates.error,
        });
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Could not change subscription plan. Please try again.';
      addNotification({
        message: apiMessage,
        level: notificationStates.error,
      });
      if (error?.response?.data?.domesticPaymentMethod) {
        setIsDomesticPaymentMethodWarningModalOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleRazorpaySubscription = async ({
    plan,
    totalSeats = 2,
    callback,
    modifySeats = false,
  }) => {
    try {
      if (!selectedCurrency) {
        setIsCurrencyModalOpen(true);
        return;
      }
      setIsLoading(true);
      const functions = firebase.functions();
      const createRazorpaySubscription = functions.httpsCallable(
        'createRazorpaySubscription'
      );

      const selectedPlanId = getPlanIdForCurrency({
        tierId: plan.id,
        billingCycle: selectedBillingCycle,
        currencyCode: selectedCurrency,
      });
      if (!selectedPlanId) {
        throw new Error(
          `Razorpay planId missing for tier=${plan?.id}, billingCycle=${selectedBillingCycle}`
        );
      }

      const result = await createRazorpaySubscription({
        planId: selectedPlanId,
        companyId: companyId,
        seats: totalSeats,
      });

      if (result?.data?.success) {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          subscription_id: result.data.subscriptionId,
          handler: async (response) => {
            if (response.razorpay_payment_id) {
              if (modifySeats) {
                await addModifySeatsFlag();
              }
              if (callback) {
                await callback();
              }
              window.location.href = `/dashboard-overview?subscription=success&paymentId=${response.razorpay_payment_id}`;
            }
          },
          name: plan.name,
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
      return;
    } catch (error) {
      console.log(error, '>>> index.jsx <<<', 'line-279');
    } finally {
      chosenPlanRef.current = null;
      setIsLoading(false);
    }
  };

  const handleCancelPlan = async ({
    reload = true,
    subscriptionId,
    message,
  }) => {
    try {
      const subscriptionIdToCancel =
        subscriptionId || subscriptionData?.subscriptionId;
      setIsLoading(true);
      const response = await httpClient.post('/cancelSubscriptionPlan', {
        subscriptionId: subscriptionIdToCancel,
      });
      const result = response.data;
      if (result.success) {
        const messageToDisplay =
          message || 'Subscription cancelled successfully.';
        addNotification({
          message: messageToDisplay,
          level: notificationStates.success,
          autoDismiss: 4,
        });
        if (reload) {
          history.push('/dashboard-overview');
          window.location.reload();
        }
      }
    } catch (error) {
      console.log(error, '>>> index.jsx <<<', 'line-415');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactUs = () => {
    setIsContactUsModalOpen(true);
  };
  const isSubscriptionCancelled = () => {
    const isInactiveSubscription = nonActiveSubscriptionStates.includes(
      subscriptionData?.status
    );
    const isSubscriptionEndDateInFuture =
      subscriptionData?.subscriptionEndDate?.toDate() > new Date();
    return isInactiveSubscription && isSubscriptionEndDateInFuture;
  };
  const handleRefundForDomesticPayment = async () => {
    try {
      setIsLoading(true);
      const response = await httpClient.post(
        '/initiateRefundForDomesticPayments',
        {
          subscriptionId: subscriptionIdToCancelRef.current,
        }
      );
      const result = response.data;
      if (result.success) {
        addNotification({
          message: 'Refund for domestic payment initiated successfully.',
          level: notificationStates.success,
          autoDismiss: 4,
        });
      }
    } catch (error) {
      addNotification({
        message: 'Failed to initiate refund for domestic payment.',
        level: notificationStates.error,
        autoDismiss: 4,
      });
      console.log(error, '>>> index.jsx <<<', 'line-420');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="subscription-page">
      <div className="subscription-container">
        {isLoading && <Spinner overlay={true} />}
        {/* Header */}
        <div className="subscription-header">
          <div className="header-content">
            <h1 className="main-title">Choose Your Plan</h1>
            <p className="subtitle">
              Select the perfect plan for your team. All plans include a 14-day
              free trial.
            </p>
            <div className="currency-context">
              <span className="currency-context__label">Billing currency:</span>{' '}
              <strong>{selectedCurrency || 'Not selected'}</strong>
            </div>

            {/* Billing Toggle */}
            <BillingToggle
              selectedCycle={selectedBillingCycle}
              onCycleChange={handleBillingCycleChange}
              discount={getDiscount(selectedBillingCycle)}
            />
          </div>
        </div>

        {/* Plans Grid */}
        {canRenderPlans ? (
          <div className="plans-grid">
            {subscriptionTiers.map((plan) => (
              <SubscriptionCard
                currentActivePlanId={currentActivePlanId}
                currentInActivePlanId={currentInActivePlanId}
                key={plan.id}
                plan={plan}
                billingCycle={selectedBillingCycle}
                currencyCode={selectedCurrency}
                isSelected={selectedPlan?.id === plan.id}
                onSelect={() => handlePlanSelect(plan)}
                showCycleEndWarning={showCycleEndWarning}
                subscriptionEndDate={subscriptionEndDate}
                cancelledAt={cancelledAt}
                onModifySeats={() => {
                  chosenPlanRef.current = plan;
                  setIsModifySeatsModalOpen(true);
                }}
                onSubscribe={() => {
                  if (typeof currentActivePlanId === 'string') {
                    planToChangeRef.current = plan;
                    if (domesticPaymentMethod) {
                      setIsDomesticPaymentMethodWarningModalOpen(true);
                      return;
                    }
                    setIsChangeSubscriptionModalOpen(true);
                    return;
                  }
                  chosenPlanRef.current = plan;
                  if (isSubscriptionCancelled()) {
                    setIsExistingSubscriptionReplaceModalOpen(true);
                    return;
                  }
                  setIsCheckoutSeatsPlanModalOpen(true);
                  return;
                }}
                isLoading={isLoading}
                onContactUs={() => {
                  isEnterpriseQueryRef.current = true;
                  handleContactUs();
                }}
                onCancelPlan={() => setIsCancelSubscriptionModalOpen(true)}
              />
            ))}
          </div>
        ) : (
          <div className="currency-selection-placeholder">
            Select a currency to view available plans.
          </div>
        )}

        {/* Footer */}
        <div className="subscription-footer">
          <button className="back-button" onClick={handleBackToDashboard}>
            <i className="ni ni-bold-left"></i>
            {new URLSearchParams(window.location.search).get('from') ===
            'suspended'
              ? 'Back'
              : 'Back to Dashboard'}
          </button>

          <div className="footer-text">
            <p>
              Need help choosing?{' '}
              <span
                onClick={() => {
                  isEnterpriseQueryRef.current = false;
                  handleContactUs();
                }}
              >
                Contact our sales team
              </span>
            </p>
            <p>
              All plans include a 14-day free trial. No credit card required.
            </p>
          </div>
        </div>

        <Modal
          isVisible={isDomesticPaymentMethodWarningModalOpen}
          onClose={() => setIsDomesticPaymentMethodWarningModalOpen(false)}
        >
          <DomesticPaymentMethodWarningModal
            onClose={() => setIsDomesticPaymentMethodWarningModalOpen(false)}
            onConfirm={() => {
              setIsDomesticPaymentMethodWarningModalOpen(false);
              setIsChangeSubscriptionModalOpen(true);
            }}
          />
        </Modal>
        <Modal
          isVisible={isCancelSubscriptionModalOpen}
          onClose={() => setIsCancelSubscriptionModalOpen(false)}
        >
          <CancelSubscriptionModal
            onClose={() => setIsCancelSubscriptionModalOpen(false)}
            onConfirm={() => {
              handleCancelPlan({
                reload: true,
              });
              setIsCancelSubscriptionModalOpen(false);
            }}
          />
        </Modal>
        <Modal
          isVisible={isChangeSubscriptionModalOpen}
          onClose={() => setIsChangeSubscriptionModalOpen(false)}
        >
          <ChangeSubscriptionModal
            newPlan={planToChangeRef.current}
            subscriptionData={subscriptionData}
            billingCycle={selectedBillingCycle}
            currencyCode={selectedCurrency}
            onClose={() => {
              setIsChangeSubscriptionModalOpen(false);
              planToChangeRef.current = null;
            }}
            onConfirm={() => {
              setIsChangeSubscriptionModalOpen(false);
              handleChangeSubscriptionPlan();
            }}
          />
        </Modal>
        <Modal
          isVisible={isContactUsModalOpen}
          onClose={() => setIsContactUsModalOpen(false)}
        >
          <ContactUsModal
            onClose={() => setIsContactUsModalOpen(false)}
            isEnterpriseQuery={isEnterpriseQueryRef.current}
          />
        </Modal>
        <Modal
          isVisible={isExistingSubscriptionReplaceModalOpen}
          onClose={() => setIsExistingSubscriptionReplaceModalOpen(false)}
        >
          <ExistingSubscriptionReplaceModal
            onConfirm={() => {
              setIsExistingSubscriptionReplaceModalOpen(false);
              setIsCheckoutSeatsPlanModalOpen(true);
            }}
            onClose={() => setIsExistingSubscriptionReplaceModalOpen(false)}
          />
        </Modal>
        <Modal
          isVisible={isCheckoutSeatsPlanModalOpen}
          onClose={() => setIsCheckoutSeatsPlanModalOpen(false)}
        >
          <CheckoutSeatsPlanModal
            subscriptionData={subscriptionData}
            plan={chosenPlanRef.current}
            billingCycle={selectedBillingCycle}
            currency={selectedCurrency}
            onClose={() => setIsCheckoutSeatsPlanModalOpen(false)}
            onConfirm={(totalSeats) => {
              setIsCheckoutSeatsPlanModalOpen(false);
              handleRazorpaySubscription({
                plan: chosenPlanRef.current,
                totalSeats,
              });
            }}
          />
        </Modal>
        <Modal
          isVisible={isModifySeatsModalOpen}
          onClose={() => setIsModifySeatsModalOpen(false)}
        >
          <ModifySeatsModal
            onClose={() => setIsModifySeatsModalOpen(false)}
            subscriptionData={subscriptionData}
            plan={chosenPlanRef.current}
            billingCycle={selectedBillingCycle}
            currency={selectedCurrency}
            onConfirm={(totalSeats) => {
              setIsModifySeatsModalOpen(false);
              subscriptionIdToCancelRef.current =
                subscriptionData?.subscriptionId;
              handleRazorpaySubscription({
                plan: chosenPlanRef.current,
                totalSeats,
                modifySeats: true,
                callback: async () => {
                  await handleCancelPlan({
                    reload: false,
                    subscriptionId: subscriptionIdToCancelRef.current,
                    message: 'Previous subscription cancelled successfully.',
                  });
                  await handleRefundForDomesticPayment();
                },
              });
            }}
          />
        </Modal>
        <Modal
          isVisible={isCurrencyModalOpen}
          className="currency-selection-modal"
          hideCloseButton={geoLookupFailed}
          disableBackdropClick={geoLookupFailed}
          disableEscapeKey={geoLookupFailed}
          onClose={() => {
            setPendingCurrency(selectedCurrency);
            setIsCurrencyModalOpen(false);
          }}
        >
          <div className="currency-modal">
            <h3 className="currency-modal__title">Choose payment currency</h3>
            <p className="currency-modal__description">
              Choose the currency in which you want to make the payment.
            </p>
            <div className="currency-modal__actions">
              <button
                type="button"
                className={`currency-modal__btn ${pendingCurrency === CURRENCY.INR ? 'currency-modal__btn--active' : ''}`}
                onClick={() => handleCurrencyChoice(CURRENCY.INR)}
              >
                INR
              </button>
              <button
                type="button"
                className={`currency-modal__btn ${pendingCurrency === CURRENCY.USD ? 'currency-modal__btn--active' : ''}`}
                onClick={() => handleCurrencyChoice(CURRENCY.USD)}
              >
                USD
              </button>
            </div>
            {pendingCurrency === CURRENCY.USD ? (
              <div className="currency-modal__warning">
                If you are making this payment from India, e-mandate in USD
                requires an internationally issued card.
              </div>
            ) : null}
            <div className="currency-modal__footer">
              <button
                type="button"
                className="currency-modal__confirm"
                onClick={handleConfirmCurrencyChoice}
                disabled={!pendingCurrency}
              >
                Confirm
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SubscriptionPage;
